import { OperationResult, Reason, } from '@js-sdk/playback';
import { HarmonyErrors } from '../../enums/errors';
import { TrackPlayerAPIClientError } from '../../error/tp_service/client';
import { resolveManifestFile } from './resolve_manifest_file';
/**
 * A representation of the TP-API State Machine as a Context (i.e. SmartList).)
 */
export class TrackPlayerServiceContext {
    /**
     *
     * @param uri - The URI of the list.
     * @param manifestFormats - A list of supported formats.
     */
    constructor(uri, manifestFormats) {
        /**
         * The State Machine object.
         */
        this._stateMachine = null;
        /**
         * The current state from the State Machine
         */
        this._currentState = null;
        /**
         * The index of the current state in the state machine.
         */
        this._currentStateIndex = null;
        /**
         * The state of whether the current track is paused or playing.
         *
         * Used for reporting.
         */
        this._pausedState = false;
        /**
         * The initial position of the first track in the context.
         */
        this._initialPlaybackPosition = null;
        this._uri = uri;
        this._manifestFormats = manifestFormats;
    }
    static create(uri, manifestFormats) {
        return new TrackPlayerServiceContext(uri, manifestFormats);
    }
    /**
     * Sets the internal paused flag of the context.
     *
     * This is used in order to make sure that the state reference generated by
     * the context contains the correct `paused` value.
     *
     * @param state - The paused state.
     */
    setPaused(state) {
        this._pausedState = state;
    }
    /**
     * Get the pause state of the current track.
     *
     * @return `true` if the current track is paused, otherwise `false`
     */
    isPaused() {
        // if currentStateIndex < 0 use paused from advance transition
        if (this._currentStateIndex &&
            this._currentStateIndex < 0 &&
            this._currentState &&
            this._currentState.transitions.advance) {
            return !!this._currentState.transitions.advance.paused;
        }
        return this._pausedState;
    }
    /**
     * Sets the position of the first track that will be loaded from the context.
     *
     * @param value - The position value in milliseconds.
     */
    setInitialPosition(value) {
        this._initialPlaybackPosition = value;
    }
    /**
     * Sets the current State Machine
     *
     * @param stateMachine - The new state machine.
     */
    setStateMachine(stateMachine) {
        this._stateMachine = stateMachine;
    }
    /**
     * Sets the device id for logging purposes
     *
     * @param deviceId - new device id
     */
    setDeviceId(deviceId) {
        this._deviceId = deviceId;
    }
    /**
     * Sets the referred state as the starting state for the context.
     *
     * @param stateRef - The reference to the new state.
     */
    startAtState(stateRef) {
        // We create a fake state so a call to next() will proceed to the actual
        // first state.
        const state = this._stateMachine && this._stateMachine.states[stateRef.state_index];
        if (!state) {
            throw new TrackPlayerAPIClientError(HarmonyErrors.TP_MISSING_INITIAL_STATE, 'Invalid state reference.');
        }
        const transitions = state.transitions;
        this._currentStateIndex = -1;
        this._currentState = {
            decoy: true,
            paused: !!stateRef.paused,
            track: -1,
            state_id: null,
            transitions: {
                advance: stateRef,
                show_next: transitions.show_next,
                show_prev: transitions.show_prev,
                skip_next: stateRef,
                skip_prev: transitions.skip_prev,
            },
        };
    }
    /**
     * Switches the current state.
     *
     * @param internalStateRef - The reference to the new state.
     */
    setCurrentState(internalStateRef) {
        const index = internalStateRef.state_index;
        this._currentStateIndex = index;
        this._currentState = this._stateMachine && this._stateMachine.states[index];
    }
    /**
     * Returns the state machine.
     *
     * @return current state machine
     */
    getStateMachine() {
        return this._stateMachine;
    }
    /**
     * Returns the current internal state reference
     *
     * @return current internal state reference
     */
    getInternalStateRef() {
        let paused = this._pausedState;
        let stateIndex = this._currentStateIndex;
        if (!stateIndex) {
            return null;
        }
        if (stateIndex && stateIndex < 0) {
            // Dummy state, we want that next one.
            const ref = this._currentState && this._currentState.transitions.advance;
            if (ref) {
                stateIndex = ref.state_index;
                paused = !!ref.paused;
            }
        }
        return {
            paused: paused,
            state_index: stateIndex,
        };
    }
    /**
     * Returns the state reference to the current state.
     *
     * @return The state reference.
     */
    getStateRef() {
        const stateMachine = this._stateMachine;
        if (!stateMachine || this._currentStateIndex === null) {
            return null;
        }
        let state;
        let paused;
        if (this._currentStateIndex < 0) {
            // Dummy state, we want that next one.
            const ref = this._currentState && this._currentState.transitions.advance;
            if (ref) {
                state = stateMachine.states[ref.state_index];
                paused = ref.paused;
            }
        }
        else {
            state = stateMachine.states[this._currentStateIndex];
            paused = this._pausedState;
        }
        if (!state) {
            // If we don't have a state, just return a null state ref, which is valid.
            return null;
        }
        return {
            state_machine_id: stateMachine.state_machine_id,
            state_id: state.state_id,
            paused: !!paused,
        };
    }
    /**
     * Returns metadata for the current track.
     *
     * @return The metadata for the current track.
     */
    getCurrentTrack() {
        const stateMachine = this._stateMachine;
        if (!stateMachine || this._currentStateIndex === null) {
            return null;
        }
        let state;
        if (this._currentStateIndex < 0) {
            // Dummy state, we want that next one.
            const ref = this._currentState && this._currentState.transitions.advance;
            if (ref)
                state = stateMachine.states[ref.state_index];
        }
        else {
            state = stateMachine.states[this._currentStateIndex];
        }
        return !state ? null : stateMachine.tracks[state.track];
    }
    /**
     * @inheritDoc
     */
    startAt() {
        // No op: we always set the context index.
        return Promise.resolve(OperationResult.SUCCESS);
    }
    /**
     * @inheritDoc
     */
    setShuffle() {
        // No unshuffling
        return Promise.resolve(OperationResult.SUCCESS);
    }
    /**
     * @inheritDoc
     */
    setRepeatMode() {
        return Promise.resolve(OperationResult.SUCCESS);
    }
    /**
     * @inheritDoc
     */
    next(opParams) {
        const currentState = this._currentState;
        let stateRef = null;
        if (currentState) {
            const transitions = currentState.transitions;
            switch (opParams.reason) {
                case Reason.FORWARD_BUTTON:
                    if ('skip_next' in transitions) {
                        stateRef = transitions.skip_next;
                    }
                    break;
                default:
                    if ('advance' in transitions) {
                        stateRef = transitions.advance;
                    }
                    break;
            }
        }
        return this._transitionTo(opParams, stateRef, false);
    }
    /**
     * @inheritDoc
     */
    peekNext(opParams) {
        const currentState = this._currentState;
        let stateRef = null;
        if (currentState) {
            const transitions = currentState.transitions;
            switch (opParams.reason) {
                case Reason.FORWARD_BUTTON:
                    if ('skip_next' in transitions) {
                        stateRef = transitions.skip_next;
                    }
                    break;
                default:
                    if ('advance' in transitions) {
                        stateRef = transitions.advance;
                    }
                    break;
            }
        }
        return this._transitionTo(opParams, stateRef, true);
    }
    /**
     * @inheritDoc
     */
    previous(opParams) {
        const currentState = this._currentState;
        let stateRef = null;
        if (currentState) {
            const transitions = currentState.transitions;
            stateRef = transitions.skip_prev;
        }
        return this._transitionTo(opParams, stateRef, false);
    }
    /**
     * Returns whether the current track allows seeking.
     *
     * @return True if we can seek for the current track, false otherwise.
     */
    allowSeeking() {
        return !!this._currentState && !this._currentState.disallow_seeking;
    }
    /**
     * Moves the context to a new state, and returns the track information for the
     * new state.
     *
     * @param stateRef - The state reference to the new state.
     * @param opParams - Optional parameteres for the operation.
     * @param dryRun - If true, it will only return the track information
     *     for the new state without moving the actual reference in the context.
     * @return The track information for the new state.
     */
    _transitionTo(opParams, stateRef, dryRun = false) {
        const reason = opParams.reason;
        const listConstants = opParams.listConstants;
        if (!stateRef) {
            return Promise.resolve(listConstants.FORBIDDEN);
        }
        const stateMachine = this._stateMachine;
        if (!stateMachine) {
            return Promise.resolve(listConstants.NULL_VALUE);
        }
        const states = stateMachine.states;
        const nextState = states[stateRef.state_index];
        if (!nextState) {
            return Promise.resolve(listConstants.NULL_VALUE);
        }
        const item = stateMachine.tracks[nextState.track];
        if (!item || !item.metadata || !item.metadata.uri) {
            return Promise.resolve(listConstants.NULL_VALUE);
        }
        if (!this._currentState) {
            return Promise.resolve(listConstants.NULL_VALUE);
        }
        const prevState = this._currentState;
        const paused = prevState.decoy ? !!prevState.paused : stateRef.paused;
        if (!dryRun) {
            this._currentState = nextState;
            this._currentStateIndex = stateRef.state_index;
            this._pausedState = !!paused;
        }
        let position = 0;
        if (!dryRun && this._initialPlaybackPosition !== null) {
            // The initial position was set externally for the first track. We use that
            // value instead of any position value that might have been set in the state
            // machine.
            position = this._initialPlaybackPosition;
            this._initialPlaybackPosition = null;
        }
        else if ('initial_playback_position' in nextState) {
            position = nextState.initial_playback_position || 0;
        }
        const file = resolveManifestFile(this._manifestFormats, item);
        if (!file) {
            return Promise.resolve(listConstants.NULL_VALUE);
        }
        let contextURI = this._uri;
        if (item.metadata && item.metadata.context_uri) {
            contextURI = item.metadata.context_uri;
        }
        // Calculate some endsong data.
        const logReason = reason;
        const playedThreshold = item.ms_played_until_update;
        const track = {
            uri: file.uri,
            playableURI: file.uri,
            fileId: file.fileId,
            resolvedURL: file.resolvedURL,
            playable: file.playable,
            isAd: file.isAd,
            format: file.format,
            mediaType: file.mediaType,
            noManifest: file.noManifest,
            options: {
                position: position,
                paused: paused,
                playedThreshold: playedThreshold,
            },
            logData: {
                noLog: !!file.noLog,
                noTSV: !!file.noTSV,
                noStats: !!file.noStats,
                deviceId: this._deviceId,
                playbackId: nextState.state_id ? nextState.state_id : undefined,
                reason: logReason,
                displayTrack: file.uri,
                playContext: contextURI,
                impressionURL: file.impressionURL,
                impressionURLs: file.impressionURLs,
                format: {
                    codec: file.format,
                    bitrate: file.bitrate,
                },
                uriType: file.uriType,
            },
            // Used to compare the track when sending state updates.
            stateId: nextState.state_id ? nextState.state_id : undefined,
        };
        return Promise.resolve(track);
    }
}
//# sourceMappingURL=context.js.map